import React from 'react';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

interface Props {
  open: boolean;
  kind: 'audio' | 'video';
  name: string;
  saving: boolean;
  error?: string | null;
  onNameChange: (name: string) => void;
  onCancel: () => void;
  onSave: () => void;
}

// Shared by the source-clip and timeline-clip editors so saving a reusable
// selection has the same naming and cancellation flow in both tabs.
export default function SaveClipDialog({
  open,
  kind,
  name,
  saving,
  error,
  onNameChange,
  onCancel,
  onSave,
}: Props) {
  return (
    <Dialog open={open} onClose={() => { if (!saving) onCancel(); }} fullWidth maxWidth="xs">
      <DialogTitle>Name the new {kind} clip</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          fullWidth
          label="Clip name"
          placeholder="e.g. Opening hook"
          value={name}
          onChange={(event) => onNameChange(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && name.trim() && !saving) {
              event.preventDefault();
              onSave();
            }
          }}
          sx={{ mt: 0.5 }}
        />
        {error && (
          <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.75, fontWeight: 600 }}>
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={onSave} disabled={!name.trim() || saving}>
          {saving ? <CircularProgress size={16} color="inherit" /> : 'Save new clip'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
